import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { motion } from "framer-motion";

import { useApprovalsModalViewModel } from "../../viewModels/useApprovalsModalViewModel";
// API

// COMPONENTS
import {
	BaseInput,
	DocumentUploader,
	SecondaryButton,
	PrimaryButton,
} from "../../../../../../components";

// TYPES
import { BaseModalProps } from "../../../../types";

const modalVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
	exit: { opacity: 0 },
};

export type FormValues = {
	poNumber: string | null;
	requiresCreditCardProcessing: boolean;
};

const EnterPoNumberModal = <T,>({ onClose }: BaseModalProps<T>) => {
	//// STATES ////
	const [isSubmittingLoading, setIsSubmittingLoading] =
		useState<boolean>(false);

	//// HOOKS ////
	const {
		fields,
		orderDetails,
		addPurchaseOrderInputAndCreditCardInfoMutation,
		activeUuid,
		purchaseOrderDetails,
	} = useApprovalsModalViewModel();

	///////////////////////////////////////////////////////////////////////// FORM SETUP ////////////////////////////////////////////////////////////////////
	const methods = useForm<FormValues>({
		mode: "onChange",
		defaultValues: {
			poNumber: purchaseOrderDetails?.purchaseOrder?.number || "",
			requiresCreditCardProcessing:
				purchaseOrderDetails?.purchaseOrder?.c_requiresCreditCard ||
				false,
		},
	});

	const { handleSubmit, watch } = methods;
	const poNumber = watch("poNumber");

	const onSubmit = async (formData: Record<string, any>) => {
		setIsSubmittingLoading(true);
		const { poNumber, requiresCreditCardProcessing } = formData;

		await addPurchaseOrderInputAndCreditCardInfoMutation.mutateAsync({
			salesOrderUuid: activeUuid as string,
			poNumber: poNumber,
			customerUuid: fields?.enterPoNumberModal?.customerUuid,
			dateOrder: orderDetails.dateOrder,
			purchaseOrderUuid: purchaseOrderDetails?.purchaseOrder?.number
				? purchaseOrderDetails?.purchaseOrder?.uuid
				: null,
			c_requiresCreditCard: requiresCreditCardProcessing,
		});

		setIsSubmittingLoading(false);
		onClose();
	};

	/////////////////////////////////////////////////////////////////// FIELDS FROM SETTINGS ////////////////////////////////////////////////////////////////////

	const poInputInputFields = fields?.enterPoNumberModal?.poNumberInput;
	const creditCardRequiredInputFields =
		fields?.enterPoNumberModal?.creditCardProcessing;
	const isDocumentUploaderVisible =
		fields?.enterPoNumberModal?.documentUploader.isVisible;

	const saveButton = fields?.enterPoNumberModal?.saveButton;
	const cancelButton = fields?.enterPoNumberModal?.cancelButton;

	/////////////////////////////////////////////////////////////////// DOCUMENT UPLOADER ////////////////////////////////////////////////////////////////////

	const [selectedFile, setSelectedFile] = useState(null);
	const [uploading, setUploading] = useState<boolean>(false);

	const handleFileChange = (event: any) => {
		const file = event.target.files[0];
		if (file) {
			setSelectedFile(file);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile) return;

		setUploading(true);

		// Create FormData to send file
		const formData = new FormData();
		formData.append("file", selectedFile);

		try {
			// Replace with your actual upload endpoint
			const response = await fetch("/api/upload", {
				method: "POST",
				body: formData,
			});

			if (response.ok) {
				alert("File uploaded successfully!");
				setSelectedFile(null);
			} else {
				alert("Upload failed");
			}
		} catch (error) {
			console.error("Error uploading file:", error);
			alert("Error uploading file");
		} finally {
			setUploading(false);
		}
	};
	return (
		fields && (
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<motion.div
						className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 "
						variants={modalVariants}
						initial="hidden"
						animate="visible"
						exit="hidden"
						onClick={onClose}
					>
						<div className="bg-white text-left min-w-[790px]   relative rounded-lg p-5 flex flex-col justify-between gap-y-5">
							<div
								onClick={(e) => e.stopPropagation()}
								className=" flex flex-col justify-between gap-y-5"
							>
								<h3 className="font-omnes-medium">
									{"Edit PO Details"}
								</h3>
								<BaseInput
									labelTextColor="text-gray-900"
									field={poInputInputFields}
									menuPlacement={"bottom"}
								/>
								<BaseInput
									labelTextColor="text-gray-900"
									field={creditCardRequiredInputFields}
									menuPlacement={"bottom"}
								/>
								{isDocumentUploaderVisible && (
									<DocumentUploader />
								)}
							</div>
							<div className="flex justify-end items-center mt-6">
								<div className="flex justify-end gap-x-3">
									<SecondaryButton
										text={cancelButton.label}
										onClick={onClose}
									/>
									<PrimaryButton
										onClick={handleSubmit(onSubmit)}
										isDisabled={!poNumber}
										disabledButtonClasses="bg-gray-400 rounded-full"
										text={saveButton.label}
										type="button"
									/>
								</div>
							</div>
						</div>
					</motion.div>
				</form>
			</FormProvider>
		)
	);
};

export default EnterPoNumberModal;
